if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // CRITICAL: Store original thumbnail order FIRST, before ANY other operations
        // This must be the very first thing we do to prevent any code from changing the order
        const thumbnailList = this.elements.thumbnails ? this.elements.thumbnails.querySelector('.thumbnail-list') : null;
        const originalThumbnailOrder = thumbnailList ? Array.from(thumbnailList.children) : [];
        const originalThumbnailIds = originalThumbnailOrder.map(child => child.id);
        
        const clickedMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!clickedMedia) return;
        
        const viewerList = this.elements.viewer.querySelector('.product__media-list');
        if (!viewerList) return;
        
        // Remove active class from ALL media items
        viewerList.querySelectorAll('.product__media-item').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Set clicked media as active - NO DOM manipulation, just set the class
        // The active image can be anywhere in the list, not just first-child
        // IMPORTANT: Do NOT move elements in DOM - keep original order
        clickedMedia.classList.add('is-active');
        
        // Force a reflow to ensure CSS is applied immediately
        void clickedMedia.offsetHeight;

        this.preventStickyHeader();
        this.playActiveMedia(clickedMedia);

        // Update thumbnail active state - but DO NOT change thumbnail order
        // IMPORTANT: Do NOT move thumbnails in DOM - keep original order
        if (!this.elements.thumbnails || !thumbnailList) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        
        // Call setActiveThumbnail first (this might trigger scroll events)
        this.setActiveThumbnail(activeThumbnail);
        
        // CRITICAL: Check and restore original thumbnail order AFTER ALL operations
        // This must be done after setActiveThumbnail and any other operations
        // Use setTimeout with 0 delay to ensure this runs after any synchronous operations
        // and requestAnimationFrame to ensure it runs after any DOM updates
        setTimeout(() => {
          requestAnimationFrame(() => {
            if (!thumbnailList) return;
            
            const currentOrder = Array.from(thumbnailList.children);
            const currentOrderIds = currentOrder.map(child => child.id);
            const orderChanged = originalThumbnailIds.some((originalId, index) => {
              return originalId !== currentOrderIds[index];
            });
            
            if (orderChanged) {
              // Restore original order by reordering elements
              // Process from end to beginning to avoid index shifting issues
              for (let i = originalThumbnailOrder.length - 1; i >= 0; i--) {
                const originalChild = originalThumbnailOrder[i];
                const currentIndex = currentOrderIds.indexOf(originalChild.id);
                
                if (currentIndex !== i && currentIndex !== -1) {
                  // Element is in wrong position, move it to correct position
                  const referenceNode = i < originalThumbnailOrder.length - 1 
                    ? originalThumbnailOrder[i + 1] 
                    : null;
                  
                  if (referenceNode && referenceNode.parentNode === thumbnailList) {
                    thumbnailList.insertBefore(originalChild, referenceNode);
                  } else if (!referenceNode) {
                    // Last element - append to end
                    thumbnailList.appendChild(originalChild);
                  }
                  
                  // Update currentOrderIds after moving
                  currentOrderIds.splice(currentIndex, 1);
                  currentOrderIds.splice(i, 0, originalChild.id);
                }
              }
            }
          });
        }, 0);
        
        if (activeThumbnail) {
          this.announceLiveRegion(clickedMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
